'use client';

import { useSuspenseQuery } from '@tanstack/react-query';
import { Icon } from '@/components/entities/Sprite/Icon';
import { ShowAllButton } from '@/components/shared';
import { SliderWrapper } from '@/components/shared/SliderWrapper';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';
import { DEFAULT_LOBBY_PARAMS, lobbyQueryOptions } from '@/queries/lobby/lobby.queries';
import { cn } from '@/utils/cn';
import { capitalizeFirstLetter } from '@/utils/texts.utils';
import GameCard from '../GameCard';

type Props = {
  categoryName: string;
  categoryId: string;
  className?: string;
  priority?: boolean;
};

export const GameCategoryRender = ({
  categoryName,
  categoryId,
  className,
  priority = false,
}: Props) => {
  const { data: gamesData } = useSuspenseQuery(lobbyQueryOptions.gamesByCategory({ filters: { categoryId } }));

  if (gamesData?.items.length === 0) {
    return null;
  }

  const categoryHref = AppRoutes.categoryIdPaged(categoryId);

  return (
    <div id={categoryId} className={cn('mb-4.25 last:-mb-1 lg:last:-mb-0.75 lg:mb-4.25 3xl:mb-7.25 3xl:last:mb-0', className)}>
      <Link href={categoryHref} className="inline-flex justify-between items-center mb-1.25 3xl:mb-1.25">
        {categoryName && (
          <div className="group flex items-center gap-1">
            <div className="relative flex items-center gap-1 h-6 font-medium text-[1.125rem] leading-5.5 3xl:leading-6">
              <span className="text-white">{capitalizeFirstLetter(categoryName)}</span>
              <span
                aria-hidden
                className="max-lg:hidden absolute inset-0 bg-main-front-gradient text-gradient opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-active:opacity-100"
              >
                {capitalizeFirstLetter(categoryName)}

              </span>
              <Icon
                name="lobby-title-arrow"
                className="size-4 mt-px lg:mt-0.5  text-white transition-colors duration-200 group-hover:text-c100 group-active:text-c100"
              />

            </div>
          </div>
        )}
      </Link>

      <div className="relative -mx-4 overflow-x-clip lg:-mx-10 3xl:mr-0">
        <SliderWrapper>
          <div
            className="flex w-max pl-4 pr-3.5 gap-2 lg:pl-10 lg:pr-10 lg:gap-5.25 3xl:pl-10 3xl:pr-0 3xl:gap-5.25"
          >
            {gamesData.items.map((game, index) => (
              <div key={game.id} className="w-30! relative shrink-0">
                <GameCard
                  id={game.id}
                  name={game.name}
                  cover={game.cover}
                  path={AppRoutes.gamesId(game.id)}
                  variant="game-cover-small"
                  priority={priority && [0, 1, 2].includes(index)}
                  likeButtonPosition="compactLg"
                  likeButtonIconSize="compactLg"
                />
              </div>
            ))}

            {gamesData.items.length >= DEFAULT_LOBBY_PARAMS.limit && (
              <div className="w-32! cursor-pointer py-1 flex items-center justify-center shrink-0">
                <ShowAllButton href={categoryHref} className="w-full" />
              </div>
            )}
          </div>
        </SliderWrapper>

        <div className="pointer-events-none absolute top-0 right-0 w-23 h-42 z-10 bg-[linear-gradient(270deg,rgba(35,19,51,0.8)_0%,rgba(35,19,51,0.4)_50%,rgba(35,19,51,0)_100%)]" />
      </div>
    </div>
  );
};
